import styled from "styled-components";
import { useParams } from "react-router-dom";
import { ChangeEvent, FC, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import Text from "@/components/Text";
import { Input } from "@/components/Forms";
import { Dispatch, RootState } from "@/utils/store";
import { selectTeamSettings } from "@/selectors";
import useDidUpdate from "@/hooks/useDidUpdate";
import useDebounce from "@/hooks/useDebounce";
import Button from "@/components/Button";
import { hexToRgba } from "@/utils/theme";

const Holder = styled.div`
  display: flex;
  gap: 16px;
  flex: 1;
`;

export const ResetHolder = styled.div`
  background-color: ${({ theme }) => theme.colors.white};
  padding: 4px;
  border-top-left-radius: 4px;
  border-bottom-left-radius: 4px;
  border-right: 1px solid rgba(196, 196, 196, 0.2);
`;

export const ResetButton = styled(Button)`
  font-size: 11px;
  line-height: 15px;
  height: 24px;
  padding: 0 8px;
  border-radius: 4px;
  border-color: transparent;
  width: 54px;
  padding-bottom: 1px;
  background-color: ${({ theme }) => hexToRgba(theme.colors.purpleLight, 0.15)};
  &:hover,
  &:active {
    border-color: transparent;
  }
`;

export const Wrapper = styled.div`
  margin-top: 8px;
  display: flex;
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid rgba(196, 196, 196, 0.2);
  box-sizing: border-box;
  border-radius: 4px;
`;

export const StyledInput = styled(Input)`
  height: 30px;
  border: 0;
  font-size: 11px;
  font-weight: 700;
  padding: 0 12px;
`;

export const InputGroup = styled.div`
  width: auto;
  flex: 1;
  margin: 0;
  max-width: 33.33%;
`;

const StyledResetButton = styled(ResetButton)`
  width: 64px;
`;

interface Props {
  assignTopicsPeriod: string;
}

const Filter: FC<Props> = ({ assignTopicsPeriod }) => {
  const dispatch = useDispatch<Dispatch>();

  const { teamId } = useParams();

  const teamSettings = useSelector((rootState: RootState) =>
    selectTeamSettings(rootState, teamId!)
  );

  const [state, setState] = useState({
    minViews: teamSettings?.assign_topics_min_views || "",
    minIndex: teamSettings?.assign_topics_min_v7_index || "",
    minDuration: teamSettings?.assign_topics_min_duration || "",
    maxDuration: teamSettings?.assign_topics_max_duration || "",
  });

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.value === "" || /^[0-9\b]+$/.test(e.target.value)) {
      setState((prevState) => {
        return {
          ...prevState,
          [e.target.name]: e.target.value,
        };
      });
    }
  };

  const debouncedState = useDebounce(state, 500);

  useDidUpdate(() => {
    dispatch.teams.updateSettings({
      teamId: teamId!,
      updatedSettings: {
        assign_topics_min_v7_index: debouncedState.minIndex,
        assign_topics_min_views: debouncedState.minViews,
        assign_topics_min_duration: debouncedState.minDuration,
        assign_topics_max_duration: debouncedState.maxDuration,
        assign_topics_period: assignTopicsPeriod ?? "",
      },
    });
  }, [debouncedState, teamId, assignTopicsPeriod]);

  const onReset = (filterName: string) => {
    setState((prevState) => {
      return {
        ...prevState,
        [filterName]: "",
      };
    });
  };

  return (
    <Holder>
      <InputGroup>
        <Text $type="smallHeadline">Minimum duration (sec)</Text>
        <Wrapper>
          <ResetHolder>
            <StyledResetButton onClick={() => onReset("minDuration")}>
              Reset
            </StyledResetButton>
          </ResetHolder>
          <StyledInput
            placeholder="Enter number"
            name="minDuration"
            value={state.minDuration}
            onChange={onChange}
          />
        </Wrapper>
      </InputGroup>
      <InputGroup>
        <Text $type="smallHeadline">Maximum duration (sec)</Text>
        <Wrapper>
          <ResetHolder>
            <StyledResetButton onClick={() => onReset("maxDuration")}>
              Reset
            </StyledResetButton>
          </ResetHolder>
          <StyledInput
            placeholder="Enter number"
            name="maxDuration"
            value={state.maxDuration}
            onChange={onChange}
          />
        </Wrapper>
      </InputGroup>
      <InputGroup>
        <Text $type="smallHeadline">Minimum views</Text>
        <Wrapper>
          <ResetHolder>
            <StyledResetButton onClick={() => onReset("minViews")}>
              Reset
            </StyledResetButton>
          </ResetHolder>
          <StyledInput
            placeholder="Enter number"
            name="minViews"
            value={state.minViews}
            onChange={onChange}
          />
        </Wrapper>
      </InputGroup>
      <InputGroup>
        <Text $type="smallHeadline">Minimum V7 Index (%)</Text>
        <Wrapper>
          <ResetHolder>
            <StyledResetButton onClick={() => onReset("minIndex")}>
              Reset
            </StyledResetButton>
          </ResetHolder>
          <StyledInput
            placeholder="Enter number"
            name="minIndex"
            value={state.minIndex}
            onChange={onChange}
          />
        </Wrapper>
      </InputGroup>
    </Holder>
  );
};

export default Filter;
